import { Injectable, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { BcmSettingsSectionName } from '@shared/models/bcm-settings';

import { BookingV2Service } from '../dtos/booking-v2.dtos';
import { DynamicPriceDurations, EvaluatedPrice, boatDimensionsOf, durationsOf, evaluateProductPrice } from '../utils/booking-v2-dynamic-price-evaluation';
import { effectivePricingRelationId } from '../utils/booking-v2-pricing-relation';
import { withEvaluatedServicePrice } from '../utils/booking-v2-service-naming';
import { BookingV2Facade } from './booking-v2.facade';

// The identifying inputs of one evaluation pass — ids/dimensions plus a signature
// of the dynamic-service set (id:productId:quantity), never the resolved prices,
// so writing prices back can't re-trigger evaluation (no loop). Unlike the relation
// flow the product set is user-mutable, so adding/removing/re-quantifying a dynamic
// service must re-fire — hence `dynamicKey`.
interface ServicePricingContext {
    boatId: number | null;
    boatLength: number | null;
    boatWidth: number | null;
    berthId: number | null;
    pierId: number | null;
    seasonId: number | null;
    winterStorageId: number | null;
    relationId: number | null;
    dynamicKey: string;
}

// Prices the dynamic-priced lines of the "Zusätzliche Leistungen" tab against the
// booking context (boat / berth / pier / season) and writes the backend-evaluated
// `rulePrice` back onto the matching facade.services entry — so the line, the
// Rechnungsversand summary and the billing all use the real price instead of the
// static catalog base. Rules on a Beziehung see the booking's relation, or the one
// picked on the line when the rule names others (#7044); the line is named after the
// matched rule until the user types a name of their own. The season picker/gating is owned by the sibling BookingV2RelationPricingService
// (it aggregates these services' requiredEntities too).
//
// Provided at the dialog level so it shares the dialog's BookingV2Facade instance
// and survives the services tab being unmounted on tab switch.
@Injectable()
export class BookingV2ServicePricingService {

    private readonly facade = inject(BookingV2Facade);
    private readonly productsApi = inject(ProductsApiService);
    private readonly settingsFacade = inject(BcmSettingsFacade);

    // Guest-hours config the overnights calc needs.
    // Plain read — `settings()` is not a signal; it is loaded long before the dialog.
    private timeUnit() {
        return this.settingsFacade.settings()?.[BcmSettingsSectionName.DefaultBerthReservationTimeUnit] ?? null;
    }

    // Settled (protected) lines are never re-sent, so they are neither re-priced nor renamed.
    private readonly dynamicServices = computed(() =>
        this.facade.services().filter(s => s.hasDynamicPrice && s.productId != null && !s.isProtected),
    );

    // Duration/traveler inputs for one line — its own vesting period (falling back to
    // the booking range), the booking-wide traveler count. Drives night-/day-/
    // traveler-based rule factors. Open-ended lines have no fixed night count.
    private serviceDurations(s: BookingV2Service): DynamicPriceDurations {
        const dr = this.facade.dateRange();
        const from = s.serviceStart ?? dr.from;
        const to = s.serviceEndOpen ? null : (s.serviceEnd ?? dr.to);
        return durationsOf(from, to, this.facade.personsOnBoard(), this.timeUnit());
    }

    // A line seeded from a berth's Produkte (#6734) is priced against THAT berth (and
    // its Steg), not the booking's first one — otherwise a rule branching on berth or
    // pier would price berth #2's Leistung with berth #1's id.
    private berthOf(s: BookingV2Service): { berthId: number | null; pierId: number | null } {
        const berthId = s.berthOrigin?.berthId ?? this.facade.selectedBerthId();
        const berths = this.facade.selectedBerths();
        const pierId = (berths.find(b => b.id === berthId) ?? berths[0])?.pierId ?? null;
        return { berthId, pierId };
    }

    private readonly context = computed<ServicePricingContext>(() => {
        const boat = this.facade.selectedBoat();
        return {
            boatId: boat?.origin === 'existing' ? boat.id : null,
            boatLength: boat?.length ?? null,
            boatWidth: boat?.width ?? null,
            berthId: this.facade.selectedBerthId(),
            // Steg of the primary berth — the backend needs it explicitly for a pier
            // rule (it does not derive the pier from the berth).
            pierId: this.facade.selectedBerths()[0]?.pierId ?? null,
            seasonId: this.facade.selectedSeason()?.id ?? null,
            winterStorageId: this.facade.selectedWinterStorageId(),
            relationId: this.facade.selectedRelation()?.id ?? null,
            // Re-fires when a dynamic line is added/removed, its quantity changes, OR
            // its duration/traveler inputs change (folded in) — but NOT when a price is
            // written back (price is not part of the key).
            dynamicKey: this.dynamicServices().map(s => {
                const d = this.serviceDurations(s);
                const berth = this.berthOf(s);
                return `${s.id}:${s.productId}:${s.quantity}:${d.days ?? ''}:${d.overnights ?? ''}:${d.travelers ?? ''}`
                    + `:${berth.berthId ?? ''}:${berth.pierId ?? ''}:${s.pricingRelationId ?? ''}`;
            }).join('|'),
        };
    });

    constructor() {
        // debounceTime(0) coalesces a synchronous burst (e.g. adding a line then the
        // season preselect setting a season) into ONE evaluation; distinctUntilChanged
        // drops no-ops.
        toObservable(this.context)
            .pipe(
                debounceTime(0),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                switchMap(ctx => this.evaluate(ctx)),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    private evaluate(ctx: ServicePricingContext): Observable<unknown> {
        const services = this.dynamicServices();
        if (!services.length) {
            return of(null);
        }
        const boatDimensions = boatDimensionsOf(ctx.boatLength, ctx.boatWidth);

        // Keyed by the service-line `id` (NOT productId) — the list can hold two lines
        // of the same product, so the write-back must target the exact line.
        const requests: Observable<EvaluatedPrice<string>>[] = services.map(s => {
            const { berthId, pierId } = this.berthOf(s);
            const relationId = effectivePricingRelationId(s.hasDynamicPrice, s.tenantRelationCriteriaIds, s.pricingRelationId, ctx.relationId);
            return evaluateProductPrice(this.productsApi, {
                key: s.id,
                productId: s.productId as number,
                quantity: s.quantity,
                parameters: {
                    tenantRelation: relationId != null ? { id: relationId } : undefined,
                    boat: ctx.boatId != null ? { id: ctx.boatId } : undefined,
                    berth: berthId != null ? { id: berthId } : undefined,
                    pier: pierId != null ? { id: pierId } : undefined,
                    season: ctx.seasonId != null ? { id: ctx.seasonId } : undefined,
                    winterStorage: ctx.winterStorageId != null ? { id: ctx.winterStorageId } : undefined,
                },
                boatDimensions,
                durations: this.serviceDurations(s),
            });
        });

        // forkJoin over an array variable widens to Observable<unknown> in this rxjs
        // version; the cast is sound — `requests` is typed Observable<EvaluatedPrice>[].
        return forkJoin(requests).pipe(tap(results => this.applyPrices(results as EvaluatedPrice<string>[])));
    }

    private applyPrices(results: EvaluatedPrice<string>[]): void {
        const byId = new Map<string, { price: number; ruleName: string | null; dynamicPrice: Partial<BcmDynamicPriceDto> | null }>();
        for (const r of results) {
            if (r && r.price != null) {
                byId.set(r.key, { price: r.price, ruleName: r.ruleName, dynamicPrice: r.dynamicPrice });
            }
        }
        if (!byId.size) {
            return;
        }
        this.facade.services.update(list =>
            list.map(s => {
                const r = byId.get(s.id);
                return r ? withEvaluatedServicePrice(s, r) : s;
            }),
        );
    }
}
